import { Node } from './node.js'

/**
 * Lista doblemente enlazada con inserción y eliminación O(1) en ambos extremos.
 * "Doblemente enlazada" significa que cada nodo conoce tanto al siguiente como al anterior,
 * lo que permite recorrerla en ambas direcciones.
 * @template T - El tipo de los valores almacenados en la lista.
 */
export class DoublyLinkedList<T> {
  public head: Node<T> | null = null
  public tail: Node<T> | null = null
  private length: number = 0

  /**
   * Alias de length para compatibilidad con Map y Set.
   */
  get size(): number {
    return this.length
  }

  /**
   * Indica si la lista está vacía.
   */
  isEmpty(): boolean {
    return this.length === 0
  }

  /**
   * Agrega un nuevo nodo al final de la lista.
   * @param value - El valor a agregar (primitivo u objeto).
   * @returns La instancia de la lista (permite encadenamiento).
   */
  push(value: T): this {
    const node = new Node(value)

    if (!this.head) {
      this.head = node
      this.tail = node
    } else {
      node.prev = this.tail
      this.tail!.next = node
      this.tail = node
    }

    this.length++
    return this
  }

  /**
   * Elimina y retorna el valor del nodo al final de la lista.
   * Gracias al puntero prev, opera en O(1).
   * @returns El valor del nodo eliminado, o undefined si la lista está vacía.
   */
  pop(): T | undefined {
    if (!this.tail) return undefined

    const removed = this.tail

    if (this.length === 1) {
      this.head = null
      this.tail = null
    } else {
      this.tail = this.tail.prev
      this.tail!.next = null
      removed.prev = null
    }

    this.length--
    return removed.value
  }

  /**
   * Agrega un nuevo nodo al inicio de la lista.
   * @param value - El valor a agregar (primitivo u objeto).
   * @returns La instancia de la lista (permite encadenamiento).
   */
  unshift(value: T): this {
    const node = new Node(value)

    if (!this.head) {
      this.head = node
      this.tail = node
    } else {
      node.next = this.head
      this.head.prev = node
      this.head = node
    }

    this.length++
    return this
  }

  /**
   * Elimina y retorna el valor del nodo al inicio de la lista.
   * Opera en O(1) porque this.head apunta directamente al primer nodo.
   * @returns El valor del nodo eliminado, o undefined si la lista está vacía.
   */
  shift(): T | undefined {
    if (!this.head) return undefined

    const removed = this.head

    if (this.length === 1) {
      this.head = null
      this.tail = null
    } else {
      this.head = this.head.next
      this.head!.prev = null
      removed.next = null
    }

    this.length--
    return removed.value
  }

  /**
   * Retorna el nodo en el índice dado sin eliminarlo.
   * Si el índice está en la segunda mitad, recorre desde tail hacia atrás — O(n/2).
   * @param index - Índice base cero del nodo a obtener.
   * @throws {RangeError} Si el índice está fuera de rango.
   * @returns El nodo en ese índice.
   */
  get(index: number): Node<T> {
    if (index < 0 || index >= this.length) {
      throw new RangeError(
        `Index ${index} is out of bounds for list of length ${this.length}`,
      )
    }

    let current: Node<T>
    if (index <= this.length / 2) {
      current = this.head!
      for (let i = 0; i < index; i++) current = current.next!
    } else {
      current = this.tail!
      for (let i = this.length - 1; i > index; i--) current = current.prev!
    }

    return current
  }

  /**
   * Actualiza el valor del nodo en el índice dado.
   * @param index - Índice base cero del nodo a actualizar.
   * @param value - El nuevo valor.
   * @throws {RangeError} Si el índice está fuera de rango.
   */
  set(index: number, value: T): void {
    this.get(index).value = value
  }

  /**
   * Inserta un nuevo nodo en el índice especificado.
   * Delega a push/unshift en los extremos para mantener O(1).
   * @param index - Índice base cero donde insertar.
   * @param value - El valor a insertar.
   * @throws {RangeError} Si el índice está fuera de rango.
   */
  insert(index: number, value: T): void {
    if (index < 0 || index > this.length) {
      throw new RangeError(
        `Index ${index} is out of bounds for list of length ${this.length}`,
      )
    }

    if (index === 0) { this.unshift(value); return }
    if (index === this.length) { this.push(value); return }

    const node = new Node(value)
    const before = this.get(index - 1)
    const after = before.next!

    before.next = node
    node.prev = before
    node.next = after
    after.prev = node

    this.length++
  }

  /**
   * Elimina el nodo en el índice especificado y retorna su valor.
   * Delega a pop/shift en los extremos para mantener O(1).
   * @param index - Índice base cero del nodo a eliminar.
   * @throws {RangeError} Si el índice está fuera de rango.
   * @returns El valor del nodo eliminado.
   */
  remove(index: number): T {
    if (index < 0 || index >= this.length) {
      throw new RangeError(
        `Index ${index} is out of bounds for list of length ${this.length}`,
      )
    }

    if (index === 0) return this.shift()!
    if (index === this.length - 1) return this.pop()!

    const node = this.get(index)
    node.prev!.next = node.next
    node.next!.prev = node.prev
    node.next = null
    node.prev = null

    this.length--
    return node.value
  }

  /**
   * Elimina un nodo directamente por referencia, sin buscar por índice — O(1).
   * Es el primitivo clave para estructuras avanzadas como caché LRU, grafos y schedulers.
   * Advertencia: pasar un nodo que pertenece a otra lista produce comportamiento indefinido.
   * @param node - El nodo a eliminar.
   * @throws {TypeError} Si el nodo es null o undefined.
   * @returns El valor del nodo eliminado.
   */
  removeNode(node: Node<T>): T {
    if (!node) throw new TypeError('node must be a valid Node instance')
    if (node === this.head) return this.shift()!
    if (node === this.tail) return this.pop()!

    node.prev!.next = node.next
    node.next!.prev = node.prev
    node.prev = null
    node.next = null

    this.length--
    return node.value
  }

  /**
   * Busca el primer nodo cuyo valor satisface el predicado dado.
   * Funciona con objetos porque el predicado tiene control total sobre la comparación.
   * @param predicate - Función que recibe (value, index) y retorna boolean.
   * @returns El nodo que cumple el predicado, o undefined si no hay ninguno.
   */
  findNode(predicate: (value: T, index: number) => boolean): Node<T> | undefined {
    let current = this.head
    let index = 0
    while (current) {
      if (predicate(current.value, index)) return current
      current = current.next
      index++
    }
    return undefined
  }

  /**
   * Busca el primer valor que satisface el predicado dado.
   * @param predicate - Función que recibe (value, index) y retorna boolean.
   * @returns El valor del primer nodo que cumple el predicado, o undefined si no hay ninguno.
   */
  find(predicate: (value: T, index: number) => boolean): T | undefined {
    return this.findNode(predicate)?.value
  }

  /**
   * Retorna el índice del primer nodo con el valor dado, usando comparación estricta.
   * Para objetos, usar findNode() con un predicado personalizado.
   * @param value - El valor a buscar.
   * @returns El índice del nodo, o -1 si no existe.
   */
  indexOf(value: T): number {
    let current = this.head
    let index = 0
    while (current) {
      if (current.value === value) return index
      current = current.next
      index++
    }
    return -1
  }

  /**
   * Indica si la lista contiene al menos un nodo con el valor dado.
   * Para objetos, usar findNode() con un predicado personalizado.
   * @param value - El valor a buscar.
   */
  contains(value: T): boolean {
    return this.indexOf(value) !== -1
  }

  /**
   * Retorna un array con todos los valores de la lista, de cabeza a cola.
   */
  toArray(): T[] {
    const result: T[] = []
    let current = this.head
    while (current) {
      result.push(current.value)
      current = current.next
    }
    return result
  }

  /**
   * Vacía la lista en O(1), soltando todas las referencias para que el GC pueda liberarlas.
   */
  clear(): void {
    this.head = null
    this.tail = null
    this.length = 0
  }

  /**
   * Hace la lista iterable con for...of, spread y destructuring.
   */
  *[Symbol.iterator](): Iterator<T> {
    let current = this.head
    while (current) {
      yield current.value
      current = current.next
    }
  }
}